import {Button, Checkbox, Col, Collapse, Divider, Radio, Row, Space} from "antd";
import React, {useEffect, useMemo, useState} from "react";
import {IssueFilterDrawer} from "@components/issues-filter-drawer";
import {fixFilterObjectEmptyArrays, IssueFilterDrawerProps} from "@components/issues-filter-drawer/IssueFilterDrawer";
import useHasPermission from "@hooks/useHasPermission";
import {isEqual} from "underscore";
import {IssueFilters} from "@mwServices/Issue";
import {FilterIcon} from "@components/icons/Icons";
import {formatTitleCase} from "@lib/format";
import {useGetCollectionErasQuery, useGetWishlistSeriesQuery} from "@mwServices/User";
import {SCREEN, useWindowSize} from "@components/window-size-provider";
import {transformEra} from "@lib/utils";

const { Panel } = Collapse;

const WishlistFiltersContent = ({ filters, setFilters}: IssueFilterDrawerProps) => {
    const showValueRibbon = useHasPermission('est_value_ribbon');
    const seriesQuery = useGetWishlistSeriesQuery();
    const series = useMemo(() => {
        if(seriesQuery.data) return seriesQuery.data.series;
        return [];
    }, [seriesQuery]);

    const erasQuery = useGetCollectionErasQuery({is_wishlist: true});
    const eras = useMemo(() => {
        if(erasQuery.data) return erasQuery.data;
        return [];
    }, [erasQuery]);

    return (
        <>
            <Row>
                <Col span={24}>
                    <div className="font-18 font-bold" >Sort by:</div>
                    <br/>

                    <Radio.Group
                        className="pad-left-10"
                        onChange={(event) => setFilters({...filters, sort: event.target.value})}
                        value={filters.sort}
                    >
                        <Space direction="vertical">
                            <Radio value={0} >Title A-Z</Radio>
                            <Radio value={21} >Quantity (Highest to Lowest)</Radio>
                            <Radio value={showValueRibbon ? 1 : null} disabled={!showValueRibbon}>Est Value (Highest to Lowest)</Radio>
                            <Radio value={showValueRibbon ? 2 : null} disabled={!showValueRibbon}>Current Market Trend (%) (Highest to Lowest)</Radio>
                        </Space>
                    </Radio.Group>
                </Col>
            </Row>
            <Divider />

            <Row>
                <Col span={24}>
                    <div className="font-18 font-bold">Filter by:</div>

                    <Collapse className="filtersAccordion" ghost defaultActiveKey={[1,2,3,4]} expandIconPosition={"end"}>
                        <Panel header="#1s" key={1}>
                            <Space direction="vertical">
                                <Checkbox checked={!!filters.onlyFirstIssues} onChange={(event) => setFilters({...filters, onlyFirstIssues: event.target.checked})}>{formatTitleCase('Only show #1s')}</Checkbox>
                            </Space>
                        </Panel>
                        <Panel header="Regular / Variants" key={2}>
                            <Space direction="vertical">
                                <Checkbox checked={true} disabled>{formatTitleCase('Regular')}</Checkbox>
                                <Checkbox checked={!!filters.showVariants} onChange={(event) => setFilters({...filters, showVariants: event.target.checked})}>{formatTitleCase('Variants')}</Checkbox>
                            </Space>
                        </Panel>

                        <Panel header="Era" key={3}>
                            <Space direction="vertical">
                                <Checkbox
                                    checked={!filters.multipleDateRanges}
                                    onChange={(event) => {
                                        setFilters({...filters, multipleDateRanges: event.target.checked ? undefined : []});
                                    }}
                                >All</Checkbox>
                                {eras.map((era, i) => (
                                    <Checkbox
                                        key={i}
                                        checked={!filters.multipleDateRanges || filters.multipleDateRanges.findIndex(dr => !!dr.id && dr.id == era.id) !== -1}
                                        onChange={(event) => {
                                            if(!filters.multipleDateRanges && !event.target.checked){
                                                setFilters({...filters, multipleDateRanges: eras.map(transformEra).filter(item => item.id !== era.id)});
                                            } else if(filters.multipleDateRanges) {
                                                let filterDateRanges: any = filters.multipleDateRanges ? [...filters.multipleDateRanges] : [];
                                                if (event.target.checked) {
                                                    filterDateRanges = [...filterDateRanges, transformEra(era)];
                                                } else {
                                                    filterDateRanges = !!filters.multipleDateRanges ? filters.multipleDateRanges.filter(item => !!item.id && item.id !== era.id) : [];
                                                }
                                                if (filterDateRanges.length === eras.length) filterDateRanges = undefined;
                                                setFilters({...filters, multipleDateRanges: filterDateRanges});
                                            }
                                        }}
                                    >
                                        {formatTitleCase(era.name, 'Unknown Era')} ({era.count})
                                    </Checkbox>
                                ))}
                            </Space>
                        </Panel>

                        <Panel header="Series" key={4}>
                            <Space direction="vertical">
                                <Checkbox
                                    checked={!filters.series}
                                    onChange={(event) => {
                                        setFilters({...filters, series: event.target.checked ? undefined : []});
                                    }}
                                >All</Checkbox>
                                {series.map((seriesItem,i) => (
                                    <Checkbox
                                        key={i}
                                        checked={!filters.series || filters.series.indexOf(seriesItem.series_id) !== -1}
                                        onChange={(event) => {
                                            if(!filters.series && !event.target.checked){
                                                setFilters({...filters, series: series.map(item => item.series_id).filter(item => item !== seriesItem.series_id)});
                                            } else if(filters.series){
                                                let filterSeries: any = filters.series ? [...filters.series] : [];
                                                if(event.target.checked){
                                                    filterSeries = [...filterSeries, seriesItem.series_id]
                                                }else{
                                                    filterSeries = !!filters.series ? filters.series.filter(item => item !== seriesItem.series_id) : [];
                                                }
                                                if(filterSeries.length === series.length) filterSeries = undefined;
                                                setFilters({...filters, series:filterSeries});
                                            }
                                        }}
                                    >
                                        {formatTitleCase(seriesItem.series_title, 'Unknown Series')}
                                    </Checkbox>
                                ))}
                            </Space>
                        </Panel>
                    </Collapse>

                </Col>
            </Row>
        </>
    );
};

const WishlistFiltersDrawer = ({ filters, setFilters}) => {
    const windowDimensions = useWindowSize();
    const [showFilters, setShowFilters] = useState<boolean>(false);
    const [newFilters, setNewFilters] = useState<IssueFilters>(filters);

    useEffect(() => {
        if(!isEqual(newFilters, filters)) {
            setNewFilters(filters);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    const onClose = () => {
        setNewFilters(filters);
        setShowFilters(false);
    };

    return (
        <>
            <Button className="greyButton" size={"large"} onClick={() => setShowFilters(true)} >
                {windowDimensions.up(SCREEN.SM) && (<span className="filterButtonText">Filter / Sort</span>)}
                <FilterIcon className="font-12" />
            </Button>

            <IssueFilterDrawer visible={showFilters} onClose={onClose} onSearch={() => {
                setShowFilters(false);

                const fixedFilters = fixFilterObjectEmptyArrays(
                    newFilters,
                    filters,
                    ['series', 'multipleDateRanges']);

                if(!isEqual(fixedFilters, filters)) {
                    setFilters(fixedFilters);
                }else if(!isEqual(fixedFilters, newFilters)) {
                    setNewFilters(fixedFilters);
                }
            }}>
                <WishlistFiltersContent filters={newFilters} setFilters={setNewFilters}  />
            </IssueFilterDrawer>
        </>
    )
};

export default WishlistFiltersDrawer;